// Готовность кадра серии: чем спрашивать и когда ставить работу.
//
// Чистые решения по ответам сервера; сами запросы живут в ModelsPage, как и у
// ролика (см. videoDates.ts — там та же развилка на своих данных).

import type { BBox } from '../api'
import type { SeriesFrame } from '../map/seriesCatalog'
import type { RenderRequest } from './videoPrepare'

/** Чем спросить готовность слоя кадра. */
export type FrameProbe =
  /** Статичный слой (батиметрия): заказывать нечего.
   *
   *  «Статичный» здесь про СОДЕРЖАНИЕ — у слоя нет даты, — а не про наличие
   *  плиток: они рисуются лениво из GEBCO по первому запросу и приезжают
   *  секундами. Дожидается их сам кадр (`waitTilesReady`), а очередь рендера
   *  тут ни при чём — рисует их сама раздача. */
  | { kind: 'static' }
  /** Обычная справка о слое на дату и глубину. */
  | { kind: 'availability'; layer: string; depth: number }
  /** Растр вида: `availability` отвечает ему «не готово» по определению. */
  | { kind: 'raster'; speciesId: number; depth: number }
  /** Спрашивать нечего: кадр вида, а вид не выбран. */
  | { kind: 'none' }

/** Замороженный индекс отрыва и семейство механизмов: один вид работы. */
const isUpwelling = (layer: string) => layer === 'upwelling' || layer.startsWith('upw_')

/** Оверлеи БЕЗ поля за дату: геометрия из базы (рубежи, промрайоны,
 *  землетрясения) и виджет (фаза Луны). Спрашивать о них справку значило бы
 *  ждать ответа ни о чём, а получить «не готово» — потерять кадр. */
const STATIC_OVERLAYS = new Set(['eez', 'fishing_districts', 'navwarn', 'substrate', 'earthquakes', 'moon'])

/** Чем спрашивать готовность этого кадра.
 *
 *  У кадра ОВЕРЛЕЯ спрашиваем про сам оверлей, а не про подложку: подложка
 *  (батиметрия) статична, и по ней любой лёд оказался бы «готов» — в архив
 *  уехала бы картинка, под которой ничего нет. */
export function probeFor(frame: SeriesFrame, speciesId: number | null): FrameProbe {
  if (frame.kind === 'overlay') {
    const id = frame.overlays[0]
    if (!id || STATIC_OVERLAYS.has(id)) return { kind: 'static' }
    return { kind: 'availability', layer: id, depth: 0 }
  }
  if (frame.layer === 'bathymetry') return { kind: 'static' }
  if (frame.layer === 'probability') {
    return speciesId == null ? { kind: 'none' } : { kind: 'raster', speciesId, depth: frame.depth ?? 0 }
  }
  return { kind: 'availability', layer: frame.layer, depth: frame.depth ?? 0 }
}

/** Что поставить в очередь ради этого кадра; null — рисовать нечего. */
export function renderRequestFor(
  frame: SeriesFrame,
  ctx: { date: string; bbox: BBox; speciesId: number | null },
): RenderRequest | null {
  if (frame.kind === 'overlay') {
    const id = frame.overlays[0]
    if (!id || STATIC_OVERLAYS.has(id)) return null
    return { date: ctx.date, bbox: ctx.bbox, layers: [id], depth: 0, species_id: null, kind: 'raw' }
  }
  if (frame.layer === 'bathymetry') return null
  const model = frame.layer === 'probability'
  if (model && ctx.speciesId == null) return null
  return {
    date: ctx.date,
    bbox: ctx.bbox,
    layers: [frame.layer],
    depth: frame.depth ?? 0,
    species_id: model ? ctx.speciesId : null,
    // Механизмы вертикального движения считаются тем же прогоном, что и
    // замороженный индекс, — объём задают ЗАПРОШЕННЫЕ слои
    // (`prepare_pipeline.upwelling_scope`), поэтому вид работы у них общий.
    kind: model ? 'model' : isUpwelling(frame.layer) ? 'upwelling' : 'raw',
  }
}

/**
 * Чем кончилась подготовка ВОШЕДШЕГО в архив кадра.
 *
 * Исходов ровно два: тайлы были на месте или мы их дорисовали. Кадр, под
 * которым данных нет, в архив не попадает вовсе (см. map/exportSeries.ts) —
 * поэтому «нет данных» здесь не исход подготовки, а причина отсутствия, и
 * живёт она в списке пропавших (`ExcludedFrame`). Двумя способами сказать
 * одно и то же значило бы завести второй источник правды.
 */
export type FrameReadiness = 'tiles-ready' | 'rendered'
